import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import LocationPermissionPrompt from '../components/LocationPermissionPrompt';
import InGoMap from '../components/maps/InGoMap';
import { useInGoRoute } from '../components/maps/useInGoRoad';
import { useLiveLocation } from '../hooks/useLiveLocation';
import {
  NAME_AGAIN,
  geolocationFailureMessage,
  pickupPlaceFromCoords,
} from '../lib/devicePickupLocation';
import { forwardGeocodeAddress } from '../lib/reverseGeocode';
import { estimateRoadKm, haversineKm } from '../lib/routeEstimate';
import AddressSuggestInput from '../components/AddressSuggestInput';
import './bookRide.css';

/*
 * A point placed by the current location or a drag is named, never left as
 * coordinates while a name can be had (12 Oct 2026, Rio: "sometimes it use
 * codinates not the building"; InGo ADR 0055). A name that did not load is
 * asked for again every 10 s for two minutes, and put in the field if the
 * field still holds that point's coordinates. A place InGo does not deliver
 * to is named and said so.
 */
const NAME_PENDING = "The address name didn't load. Trying again.";
const NOT_SERVED = { pickup: "InGo doesn't deliver here yet.", dropoff: "InGo doesn't deliver there yet." };

/** A place as InGo books it: the address and its pin, or null when it cannot be placed. */
/** `near`: where typed words are measured from, so they place at the nearest place they name. */
async function pinFor(address, coords, near = null) {
  if (!address) return null;
  if (coords && Number.isFinite(coords.lat) && Number.isFinite(coords.lng)) {
    return { address, latitude: coords.lat, longitude: coords.lng };
  }
  const found = await forwardGeocodeAddress(address, { near });

  return found ? { address, latitude: found.lat, longitude: found.lng } : null;
}

function BackArrow() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M15.5 18.5L8.5 12l7-7.5"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function GpsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
      <circle cx="12" cy="12" r="4.5" strokeWidth="1.3" fill="none" />
      <path d="M12 3.5V7M12 17v3.5M3.5 12H7M17 12h3.5" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function IconChevron() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M9.5 7.5L14 12l-4.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** The drop-off's words: the last stop with any. */
function lastStopWords(stops) {
  const texts = stops.map((s) => String(s?.value ?? '').trim()).filter(Boolean);

  return texts[texts.length - 1] || '';
}

function createStop() {
  return { id: `${Date.now()}-${Math.random()}` };
}

function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export default function RequestDeliveryPage() {
  const navigate = useNavigate();
  const live = useLiveLocation({ mapThrottleMs: 4000 });
  const [pickup, setPickup] = useState('');
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsNotice, setGpsNotice] = useState('');
  const [dropNotice, setDropNotice] = useState('');
  const [stops, setStops] = useState([createStop()]);

  const setAt = (i, v) => {
    setStops((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], value: v };
      return next;
    });
  };

  const [routeDistanceKm, setRouteDistanceKm] = useState(null);
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);
  const [pinBusy, setPinBusy] = useState('');
  /**
   * Each pin with the words it stands for. A pin put down exactly (the
   * current location, a picked suggestion, a drag) stays where it was put
   * while its words stand; only words typed by hand are looked up. Before
   * (Rio, 5 Oct 2026: "people are more likely to make mistakes"), any edit
   * looked both fields' words up again and moved an exact pin to their first
   * hit: the current location to the school its name found, a picked Seeta to
   * another Seeta.
   */
  const placedRef = useRef({ pickup: null, dropoff: null });
  /**
   * The words in each field now. The lookups below run on words held back
   * 320 ms, and words that are no longer in the field place and clear
   * nothing: a run that still saw an empty drop-off cleared the suggestion
   * picked a moment later (the page's test, 5 Oct 2026).
   */
  const wordsNowRef = useRef({ pickup: '', dropoff: '' });
  wordsNowRef.current = { pickup: pickup.trim(), dropoff: lastStopWords(stops) };
  /** Where the phone is, for measuring typed words from (the nearest place they name). */
  const hereRef = useRef(null);
  hereRef.current = live.located ? { lat: live.lat, lng: live.lng } : null;

  /** A pin and the words it stands for, shown on the map. Null clears it. */
  const place = (field, words, point) => {
    placedRef.current[field] = point ? { words: String(words || '').trim(), lat: point.lat, lng: point.lng } : null;
    (field === 'pickup' ? setPickupCoords : setDropoffCoords)(point ? { lat: point.lat, lng: point.lng } : null);
  };

  /** The pin already placed for these very words, if any. */
  const placedFor = (field, words) => {
    const placed = placedRef.current[field];

    return placed && words && placed.words === words ? { lat: placed.lat, lng: placed.lng } : null;
  };

  /** What the line under a field says of a point just named: not delivered to, or its name still coming. */
  const sayAbout = (field, got) => {
    const say = !got.served ? NOT_SERVED[field] : got.failed ? NAME_PENDING : '';
    (field === 'pickup' ? setGpsNotice : setDropNotice)(say);
  };

  /** The name of a point that could not be named yet, asked again while its coordinates stand. */
  const laterRef = useRef({ pickup: null, dropoff: null });
  useEffect(
    () => () => {
      window.clearTimeout(laterRef.current.pickup);
      window.clearTimeout(laterRef.current.dropoff);
    },
    [],
  );
  const nameLater = (field, line, point, tried = 0) => {
    window.clearTimeout(laterRef.current[field]);
    laterRef.current[field] = window.setTimeout(async () => {
      const got = await pickupPlaceFromCoords(point.lat, point.lng, { tries: 1 });
      // Only onto those coordinates on that pin: words typed or a pin moved since win.
      const placed = placedRef.current[field];
      if (wordsNowRef.current[field] !== line.trim() || !placed || placed.lat !== point.lat || placed.lng !== point.lng) return;
      if (got.named) {
        place(field, got.line, point);
        if (field === 'pickup') setPickup(got.line);
        else setAt(0, got.line);
      }
      sayAbout(field, got);
      if (got.failed && tried + 1 < NAME_AGAIN.tries) nameLater(field, line, point, tried + 1);
    }, NAME_AGAIN.ms);
  };

  const onContinue = async (e) => {
    e.preventDefault();
    const p = pickup.trim();
    const stopTexts = stops.map((s) => String(s?.value ?? '').trim()).filter(Boolean);
    const drop = stopTexts[stopTexts.length - 1] || '';
    // An InGo client books on pins (M5): the ones chosen on the map or in the
    // list, or found now for an address typed and never picked. The price is
    // InGo's, on the road between them, at the next step but one.
    const pickupPin = await pinFor(p, placedFor('pickup', p), hereRef.current);
    const dropoffPin = await pinFor(drop, placedFor('dropoff', drop), pickupPin ? { lat: pickupPin.latitude, lng: pickupPin.longitude } : hereRef.current);
    navigate('/package-details', {
      state: { pickup, stops, pickupPin, dropoffPin },
    });
  };

  const debouncedPickup = useDebouncedValue(pickup.trim(), 320);
  const debouncedStops = useDebouncedValue(stops, 320);

  useEffect(() => {
    let cancelled = false;
    const p = debouncedPickup.trim();
    const stopTexts = debouncedStops.map((s) => (s?.value ?? '').trim()).filter(Boolean);
    const destinationText = stopTexts[stopTexts.length - 1] || '';
    const middleTexts = stopTexts.length > 1 ? stopTexts.slice(0, -1) : [];

    // Words already replaced in a field: the run for the new ones is coming.
    if (wordsNowRef.current.pickup !== p || wordsNowRef.current.dropoff !== destinationText) return undefined;

    if (!p && !destinationText) {
      setRouteDistanceKm(null);
      place('pickup', '', null);
      place('dropoff', '', null);
      return undefined;
    }

    /** A field's pin: the one placed for these words, else the words looked up (null when nothing is found). */
    const pinOf = async (field, words, near) => {
      if (!words) return null;
      const placed = placedFor(field, words);
      if (placed) return placed;
      const found = await forwardGeocodeAddress(words, { near });

      return found ? { lat: found.lat, lng: found.lng } : null;
    };

    // A pin put down while these lookups ran (a suggestion picked as the
    // half-typed words were being searched) is newer than their answer.
    const placedBefore = { ...placedRef.current };

    (async () => {
      try {
        // Typed words place at the nearest place they name: the pickup's from the
        // phone, the drop-off's from the pickup.
        const pickupGeo = await pinOf('pickup', p, hereRef.current);
        const destGeo = await pinOf('dropoff', destinationText, placedFor('pickup', p) ?? pickupGeo ?? hereRef.current);
        if (cancelled) return;

        // Only for words still in the field, onto no pin put down meanwhile;
        // words that place nowhere keep no pin from other words.
        const fresh = (field, words) => wordsNowRef.current[field] === words && placedRef.current[field] === placedBefore[field];
        if (fresh('pickup', p) && !placedFor('pickup', p)) place('pickup', p, pickupGeo);
        if (fresh('dropoff', destinationText) && !placedFor('dropoff', destinationText)) place('dropoff', destinationText, destGeo);

        // The distance only for the words in the fields now, between the pins
        // they stand for: a look-up of half-typed words that answers after a
        // pick showed the distance to another place (live, 5 Oct 2026: 69.5 km
        // to Entebbe's Seeta for a Seeta picked 18 km away).
        if (wordsNowRef.current.pickup !== p || wordsNowRef.current.dropoff !== destinationText) return;
        const from = placedFor('pickup', p) ?? pickupGeo;
        const to = placedFor('dropoff', destinationText) ?? destGeo;
        if (!from || !to) {
          setRouteDistanceKm(null);
          return;
        }

        let waypointPairs = [];
        if (middleTexts.length) {
          const midGeos = await Promise.all(middleTexts.map((t) => forwardGeocodeAddress(t, { near: from })));
          if (cancelled) return;
          waypointPairs = midGeos.filter(Boolean).map((g) => [g.lat, g.lng]);
        }

        let straight = 0;
        let prev = { lat: from.lat, lng: from.lng };
        for (const pair of waypointPairs) {
          const h = haversineKm(prev.lat, prev.lng, pair[0], pair[1]);
          if (h == null) {
            straight = null;
            break;
          }
          straight += h;
          prev = { lat: pair[0], lng: pair[1] };
        }
        if (straight != null) {
          const hLast = haversineKm(prev.lat, prev.lng, to.lat, to.lng);
          if (hLast != null) straight += hLast;
          else straight = null;
        }
        const roadKm = straight != null ? estimateRoadKm(straight) : null;
        if (!cancelled) setRouteDistanceKm(roadKm != null && roadKm > 0 ? roadKm : null);
      } catch {
        if (!cancelled) setRouteDistanceKm(null);
      }
    })();

    return () => {
      cancelled = true;
    };
    // `place` and `placedFor` work on refs and setters only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPickup, debouncedStops]);

  const hasEditablePins = Boolean(pickupCoords || dropoffCoords);

  // An InGo client books on InGo's map and road (the mobile plan's M8), the
  // pins still draggable to fine-tune them.
  const inGoRoute = useInGoRoute(pickupCoords, dropoffCoords);
  const inGoRoad = inGoRoute.polyline;
  const inGoPins = useMemo(
    () => [
      ...(pickupCoords ? [{ role: 'pickup', lat: pickupCoords.lat, lng: pickupCoords.lng }] : []),
      ...(dropoffCoords ? [{ role: 'dropoff', lat: dropoffCoords.lat, lng: dropoffCoords.lng }] : []),
    ],
    [pickupCoords, dropoffCoords],
  );

  const useGps = async () => {
    if (gpsLoading) return;
    setGpsNotice('');
    setGpsLoading(true);
    try {
      const coords = await live.refreshFromUserGesture();
      const next = { lat: coords.latitude, lng: coords.longitude };
      // The phone's own point at once, under the words standing; then under its name.
      place('pickup', pickup, next);
      const got = await pickupPlaceFromCoords(coords.latitude, coords.longitude);
      place('pickup', got.line, next);
      setPickup(got.line);
      sayAbout('pickup', got);
      if (got.failed) nameLater('pickup', got.line, next);
    } catch (err) {
      const code = typeof err?.code === 'number' ? err.code : 2;
      setGpsNotice(geolocationFailureMessage(code));
    } finally {
      setGpsLoading(false);
    }
  };

  const recomputeDistanceFromPins = (pu, drop) => {
    if (!pu || !drop) {
      setRouteDistanceKm(null);
      return;
    }
    const straight = haversineKm(pu.lat, pu.lng, drop.lat, drop.lng);
    const roadKm = straight != null ? estimateRoadKm(straight) : null;
    setRouteDistanceKm(roadKm != null && roadKm > 0 ? roadKm : null);
  };

  const onPickupDragEnd = async (lat, lng) => {
    const next = { lat, lng };
    place('pickup', pickup, next);
    recomputeDistanceFromPins(next, dropoffCoords);
    setPinBusy('pickup');
    let got;
    try {
      got = await pickupPlaceFromCoords(lat, lng);
    } catch {
      got = { line: `${lat.toFixed(5)}, ${lng.toFixed(5)}`, named: false, served: true, failed: true };
    } finally {
      setPinBusy('');
    }
    place('pickup', got.line, next);
    setPickup(got.line);
    sayAbout('pickup', got);
    if (got.failed) nameLater('pickup', got.line, next);
  };

  const onDropoffDragEnd = async (lat, lng) => {
    const next = { lat, lng };
    place('dropoff', stops[0]?.value, next);
    recomputeDistanceFromPins(pickupCoords, next);
    setPinBusy('dropoff');
    let got;
    try {
      got = await pickupPlaceFromCoords(lat, lng);
    } catch {
      got = { line: `${lat.toFixed(5)}, ${lng.toFixed(5)}`, named: false, served: true, failed: true };
    } finally {
      setPinBusy('');
    }
    place('dropoff', got.line, next);
    setAt(0, got.line);
    sayAbout('dropoff', got);
    if (got.failed) nameLater('dropoff', got.line, next);
  };

  // The road's own length once InGo's engine has answered for these pins, the
  // road the map draws and the price is made on (Rio, 5 Oct 2026: "ok go on");
  // until then the straight line's estimate, said as one.
  const byRoad = inGoRoute.distanceKm != null;
  const distanceLabel = byRoad
    ? `${inGoRoute.distanceKm.toFixed(1)} km`
    : routeDistanceKm != null && Number.isFinite(routeDistanceKm)
      ? `about ${routeDistanceKm.toFixed(1)} km`
      : '—';

  return (
    <form id="rd-delivery-form" className="br-page" onSubmit={onContinue}>
      <header className="br-nav">
        <Link to="/home" className="br-nav__back" aria-label="Back to home">
          <BackArrow />
        </Link>
        <h1 className="br-nav__title">Delivery</h1>
        <span className="br-nav__spacer" aria-hidden />
      </header>

      <div className="br-scroll">
        <div className="br-map-wrap">
          <div
            className={`br-map br-map--gmap${hasEditablePins ? ' br-map--editable' : ''}`}
            role="region"
            aria-label="Map with pickup and dropoff — drag pins to adjust"
          >
            <div className="br-map-js-layer">
              <InGoMap
                pins={inGoPins}
                route={inGoRoad}
                me={live.located ? { lat: live.lat, lng: live.lng } : null}
                onPinMoved={(role, lat, lng) => void (role === 'pickup' ? onPickupDragEnd(lat, lng) : onDropoffDragEnd(lat, lng))}
                label="Map with pickup and drop-off"
                height="100%"
              />
              {hasEditablePins ? (
                <span className="br-map-hint" aria-live="polite">
                  {pinBusy ? 'Updating address…' : 'Drag the green (pickup) or red (drop-off) pin to fine-tune'}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <LocationPermissionPrompt live={live} placement="flow" />

        <div className="br-loc-card">
          <div className="br-loc-list">
            <div className="br-loc-row flow-field--addrSuggest">
              <span className="br-loc-row__dot br-loc-row__dot--pickup" aria-hidden />
              <div className="br-loc-row__main">
                <span className="br-loc-row__label">Pickup location</span>
                <div className="br-loc-row__input">
                  <AddressSuggestInput
                    id="flow-pickup"
                    name="pickup"
                    value={pickup}
                    onChange={(v) => {
                      setGpsNotice('');
                      setPickup(v);
                      if (!String(v || '').trim()) place('pickup', '', null);
                    }}
                    onSelectSuggestion={(s) => {
                      if (s && Number.isFinite(Number(s.lat)) && Number.isFinite(Number(s.lng))) {
                        const point = { lat: Number(s.lat), lng: Number(s.lng) };
                        place('pickup', s.label, point);
                        recomputeDistanceFromPins(point, dropoffCoords);
                      }
                    }}
                    near={live.located ? { lat: live.lat, lng: live.lng } : null}
                    placeholder={pickup.trim() || (live.hasFix ? 'Current location' : 'Enter pickup address')}
                    autoComplete="street-address"
                    ariaLabel="Pickup address"
                    inline
                  />
                </div>
                <button
                  type="button"
                  className="br-loc-gps"
                  onClick={useGps}
                  disabled={gpsLoading}
                  aria-busy={gpsLoading}
                >
                  <GpsIcon />
                  {gpsLoading ? 'Finding address…' : 'Use current location'}
                </button>
                {gpsNotice ? (
                  <p className="br-geo-notice" role="alert">
                    {gpsNotice}
                  </p>
                ) : null}
              </div>
              <span className="br-loc-row__chev" aria-hidden>
                <IconChevron />
              </span>
            </div>

            <div className="br-loc-row flow-field--addrSuggest">
              <span className="br-loc-row__dot br-loc-row__dot--drop" aria-hidden />
              <div className="br-loc-row__main">
                <span className="br-loc-row__label">Drop-off location</span>
                <div className="br-loc-row__input">
                  <AddressSuggestInput
                    id="flow-dropoff"
                    name="dropoff"
                    value={stops[0]?.value ?? ''}
                    onChange={(v) => {
                      setDropNotice('');
                      setAt(0, v);
                      if (!String(v || '').trim()) place('dropoff', '', null);
                    }}
                    onSelectSuggestion={(s) => {
                      if (s && Number.isFinite(Number(s.lat)) && Number.isFinite(Number(s.lng))) {
                        const point = { lat: Number(s.lat), lng: Number(s.lng) };
                        place('dropoff', s.label, point);
                        // The figure for the place picked at once, as a drag does.
                        recomputeDistanceFromPins(pickupCoords, point);
                      }
                    }}
                    near={pickupCoords ?? (live.located ? { lat: live.lat, lng: live.lng } : null)}
                    placeholder="Enter drop-off location"
                    autoComplete="off"
                    ariaLabel="Drop-off address"
                    inline
                  />
                </div>
                {dropNotice ? (
                  <p className="br-geo-notice" role="alert">
                    {dropNotice}
                  </p>
                ) : null}
              </div>
              <span className="br-loc-row__chev" aria-hidden>
                <IconChevron />
              </span>
            </div>
          </div>
        </div>

        <section className="br-fare" aria-label="Route distance">
          <div className="br-fare__top">
            <span className="br-fare__lab">Estimated distance</span>
            <span className="br-fare__amt">{distanceLabel}</span>
          </div>
          <p className="br-delivery-hint">{byRoad ? 'By road. ' : ''}Fare and package options on the next step.</p>
        </section>
      </div>

      <div className="br-footer">
        <button type="submit" className="br-confirm">
          Continue
        </button>
      </div>
    </form>
  );
}
